import { useEffect, useMemo, useRef, useState } from "react";
import {
  useQuery,
  getPropFirmTemplates,
  getTradingStrategiesByUser,
  listTradovateAccountsForImport,
  confirmTradovateImport,
} from "wasp/client/operations";
import { Loader2, AlertCircle, Inbox, RefreshCw } from "lucide-react";
import { Button } from "../../client/components/ui/button";
import { Input } from "../../client/components/ui/input";
import { Label } from "../../client/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "../../client/components/ui/select";
import { cn } from "../../client/utils";
import useTranslation from "../../client/hooks/useTranslation";
import { useToast } from "../../client/hooks/use-toast";
import type { PropFirmTemplateSnapshot, TierSnapshot } from "../propFirmEngine";
import { ACCOUNT_STATUSES, type AccountStatus } from "../accountStatus";
import { parseCost } from "./tradovateImportCost";
import { isDirectFunded, isSubscription } from "../pricingModel";
import { initialImportStatus } from "./tradovateImportRowDefaults";
import { NEW_GROUP_VALUE, NO_GROUP_VALUE, initialRowGroupValue } from "./tradovateImportGroup";
import PredecessorLabel, { type PredecessorOption } from "../components/PredecessorLabel";

interface Props {
  connectionId: string;
  propFirmTemplateId: string | null;
  onDone: () => void;
  /**
   * Aperçu (mode démo de la synchro, build de dev uniquement) : liste de comptes
   * simulés à la place de la lecture Tradovate, et import sans appel serveur.
   * Absente = comportement normal.
   */
  demoAccounts?: Array<{ tradovateAccountId: number; name: string; apiEnv: "live" | "demo"; balance: number; suggestedTierId: string | null; confidence: "high" | "low" | "none"; isKnown?: boolean }>;
}

// Account.status values offered in the review. Source unique de vérité :
// accountStatus.ts — "funded" n'est PAS un Account.status (c'est un phaseType
// de prop firm) et produisait des comptes invisibles des filtres par statut.
// "inactive" est exclu du choix à l'import : un compte fraîchement importé est
// actif par définition (la désactivation passe par la page Comptes / la sync).
const STATUS_OPTIONS = ACCOUNT_STATUSES.filter(
  (s): s is Exclude<AccountStatus, "inactive"> => s !== "inactive",
);

interface ImportRow {
  tradovateAccountId: number;
  name: string;
  apiEnv: "live" | "demo";
  balance: number;
  tierId: string | null;
  status: AccountStatus;
  edgelyAccountName: string;
  confidence: "high" | "low" | "none";
  /** Compte déjà connu d'Edgely (rattachement / passage en funded). */
  isKnown: boolean;
  /** Saisie libre : conservée en string pour ne pas casser la frappe ("12," etc.). */
  challengeCost: string;
  /** Challenge dont ce PA est la suite (null = aucun). Voir fundedLineage.ts. */
  previousAccountId: string | null;
  /** Valeur du sélecteur de groupe : id de groupe, NEW_GROUP_VALUE ou NO_GROUP_VALUE. */
  group: string;
}

/** Seul un compte importé en funded peut être la suite d'un challenge. */
const canFollowChallenge = (status: AccountStatus) => status === "pa" || status === "live";

export default function TradovateImportReview({ connectionId, propFirmTemplateId, onDone, demoAccounts }: Props) {
  const t = useTranslation();
  const { toast } = useToast();

  const { data: templates = [] } = useQuery(getPropFirmTemplates);
  // Pas de relance au retour sur l'onglet : chaque relance refaisait 2 appels
  // /account/list et 1 snapshot de solde par compte sur le quota Tradovate.
  const listQuery = useQuery(listTradovateAccountsForImport, {
    connectionId,
    propFirmTemplateId,
  }, { enabled: !demoAccounts, refetchOnWindowFocus: false, staleTime: 60_000 });
  const { error, refetch } = listQuery;
  const data = demoAccounts ? { accounts: demoAccounts } : listQuery.data;
  const isLoading = demoAccounts ? false : listQuery.isLoading;

  const propFirm = useMemo(
    () =>
      propFirmTemplateId
        ? (templates as PropFirmTemplateSnapshot[]).find((tpl) => tpl.id === propFirmTemplateId) ?? null
        : null,
    [templates, propFirmTemplateId],
  );

  // Tiers grouped by account type (server returns PropFirmTier.id directly).
  // Ne PAS aplatir : plusieurs types de compte partagent les mêmes labels de
  // palier ("50k" existe en Flex et en Standard), et une liste plate rend donc
  // le choix impossible. Les types inactifs restent affichés : un compte déjà
  // ouvert peut porter un palier d'une offre retirée du catalogue.
  const tierGroups: Array<{ id: string; name: string; tiers: TierSnapshot[]; pricingModel: string | null }> = useMemo(
    () =>
      propFirm
        ? [...propFirm.accountTypes]
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map((at) => ({ id: at.id, name: at.name, tiers: at.tiers, pricingModel: at.pricingModel ?? null }))
            .filter((g) => g.tiers.length > 0)
        : [],
    [propFirm],
  );

  const tierCount = useMemo(
    () => tierGroups.reduce((sum, g) => sum + g.tiers.length, 0),
    [tierGroups],
  );

  // Paliers dont le type de compte est direct funded : un compte sur l'un
  // d'eux ne peut jamais être créé en "challenge" (refusé par le serveur).
  const directFundedTierIds = useMemo(
    () =>
      new Set(
        tierGroups
          .filter((g) => isDirectFunded(g.pricingModel))
          .flatMap((g) => g.tiers.map((tier) => tier.id)),
      ),
    [tierGroups],
  );

  // Libellé du palier -> modèle de tarification, pour retrouver le modèle du
  // palier SUGGÉRÉ par le serveur avant même que `directFundedTierIds` (qui
  // dépend des templates, chargés séparément de la liste des comptes) ne soit
  // disponible à l'initialisation des lignes.
  const tierPricingModel = useMemo(() => {
    const map = new Map<string, string | null>();
    for (const group of tierGroups) {
      for (const tier of group.tiers) map.set(tier.id, group.pricingModel);
    }
    return map;
  }, [tierGroups]);

  // Le trigger fermé n'affiche que le label du palier ("50k"), ce qui est tout
  // aussi ambigu que la liste plate : on résout le nom du type de compte pour
  // afficher "Flex - 50k".
  const tierDisplayLabel = useMemo(() => {
    const map = new Map<string, string>();
    for (const group of tierGroups) {
      for (const tier of group.tiers) {
        map.set(tier.id, `${group.name} - ${tier.label}`);
      }
    }
    return map;
  }, [tierGroups]);

  const { data: strategies = [] } = useQuery(getTradingStrategiesByUser);

  const [rows, setRows] = useState<ImportRow[] | null>(null);
  const [groupName, setGroupName] = useState("");
  const [strategyId, setStrategyId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Lignes dont le statut a été choisi explicitement par l'utilisateur via LE
  // SELECT DE STATUT (uniquement — le changement de palier peut aussi corriger
  // le statut vers "pa", mais c'est une correction programmatique, pas un choix
  // sur le statut lui-même, donc elle n'est jamais ajoutée ici) : le rattrapage
  // "direct funded" ci-dessous, qui arrive avec les templates (potentiellement
  // après les comptes), ne doit jamais écraser un choix explicite déjà fait.
  const userEditedStatus = useRef<Set<number>>(new Set());

  // Initialize rows once data loads.
  const accounts = (data as any)?.accounts as
    | Array<{
        tradovateAccountId: number;
        name: string;
        apiEnv: "live" | "demo";
        balance: number;
        suggestedTierId: string | null;
        confidence: "high" | "low" | "none";
        isKnown?: boolean;
        existing?: { name: string; status: AccountStatus; tierId: string | null; groupId?: string | null } | null;
        suggestedPredecessorId?: string | null;
      }>
    | undefined;
  const predecessorOptions: PredecessorOption[] = (data as any)?.predecessorCandidates ?? [];
  const groupOptions: Array<{ id: string; name: string }> = (data as any)?.groups ?? [];
  // Groupe qui contient déjà les comptes de cette prop firm : pré-sélectionné
  // sur chaque compte neuf, modifiable ligne par ligne.
  const suggestedGroupId: string | null = (data as any)?.suggestedGroupId ?? null;

  if (accounts && rows === null) {
    const newRowCount = accounts.filter((a) => !(a.isKnown ?? true)).length;
    const newRowGroup = initialRowGroupValue(suggestedGroupId, newRowCount);
    setRows(
      accounts.map((a) => ({
        tradovateAccountId: a.tradovateAccountId,
        name: a.name,
        apiEnv: a.apiEnv ?? "live",
        balance: a.balance,
        // Compte déjà connu : ses valeurs Edgely réelles, que l'import ne
        // modifie pas (rattachement seulement). Sinon détection + défaut.
        tierId: a.existing ? a.existing.tierId : a.suggestedTierId,
        // Compte neuf : le palier suggéré peut être direct funded, et
        // "challenge" par défaut mènerait alors droit dans le refus serveur.
        // `tierPricingModel` vient des templates (`getPropFirmTemplates`), qui
        // peuvent arriver après les comptes (`listTradovateAccountsForImport`) :
        // ce premier rendu utilise alors encore un modèle "one_time" par défaut
        // (`tierPricingModel` vide), d'où l'effet de correction ci-dessous une
        // fois les templates chargés.
        status:
          a.existing?.status ??
          initialImportStatus(tierPricingModel.get(a.suggestedTierId ?? ""), a.apiEnv),
        edgelyAccountName: a.existing?.name ?? a.name,
        confidence: a.confidence,
        // Défaut prudent `true` : si le serveur n'envoie pas le drapeau, on
        // n'affiche pas la colonne prix plutôt que de la proposer à tort sur
        // un compte déjà connu.
        isKnown: a.isKnown ?? true,
        challengeCost: "",
        previousAccountId: a.suggestedPredecessorId ?? null,
        // Compte connu : son groupe actuel, que l'import ne modifie pas.
        group: (a.isKnown ?? true) ? (a.existing?.groupId ?? NO_GROUP_VALUE) : newRowGroup,
      })),
    );
    if (!groupName) {
      setGroupName(`${propFirm?.name ?? "Tradovate"} - Tradovate`);
    }
  }

  // Rattrapage : si les templates (donc `directFundedTierIds`) arrivent APRÈS
  // les comptes, l'initialisation ci-dessus a résolu `tierPricingModel` sur une
  // map encore vide et a donc laissé "challenge" par défaut même pour un palier
  // direct funded. On corrige une fois les templates là, mais seulement les
  // lignes où l'utilisateur n'a PAS choisi "challenge" lui-même dans le Select
  // de statut entre-temps (`userEditedStatus`) : sinon ce choix explicite
  // serait écrasé en silence après coup. Un compte déjà connu n'est jamais
  // corrigé : il affiche son statut Edgely réel, que l'import ne modifie pas.
  useEffect(() => {
    if (!rows || directFundedTierIds.size === 0) return;
    setRows((prev) =>
      prev
        ? prev.map((r, i) =>
            !r.isKnown && !userEditedStatus.current.has(i) && r.status === "challenge" && r.tierId && directFundedTierIds.has(r.tierId)
              ? { ...r, status: "pa" as AccountStatus }
              : r,
          )
        : prev,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directFundedTierIds]);

  const updateRow = (index: number, patch: Partial<ImportRow>) => {
    setRows((prev) => (prev ? prev.map((r, i) => (i === index ? { ...r, ...patch } : r)) : prev));
  };

  const hasNewGroupRow = !!rows?.some((r) => !r.isKnown && r.group === NEW_GROUP_VALUE);

  const handleImport = async () => {
    if (!rows) return;
    if (demoAccounts) {
      toast({ title: t.tradovate.importButton });
      onDone();
      return;
    }
    setSubmitting(true);
    try {
      await confirmTradovateImport({
        connectionId,
        propFirmTemplateId,
        groupName: hasNewGroupRow ? groupName : undefined,
        strategyId: strategyId ?? undefined,
        accounts: rows.map((r) => ({
          tradovateAccountId: r.tradovateAccountId,
          name: r.name,
          apiEnv: r.apiEnv,
          tierId: r.tierId,
          status: r.status,
          edgelyAccountName: r.edgelyAccountName,
          balance: r.balance,
          // Virgule décimale acceptée (clavier FR). Seules les valeurs finies
          // et positives partent : une saisie inexploitable équivaut à "non
          // renseigné" plutôt qu'à un envoi rejeté par le serveur.
          ...(!r.isKnown && parseCost(r.challengeCost) != null
            ? { challengeCost: parseCost(r.challengeCost) as number }
            : {}),
          ...(!r.isKnown && canFollowChallenge(r.status) && r.previousAccountId
            ? { previousAccountId: r.previousAccountId }
            : {}),
          ...(r.isKnown
            ? {}
            : r.group === NEW_GROUP_VALUE
              ? { newGroup: true }
              : { groupId: r.group === NO_GROUP_VALUE ? null : r.group }),
        })),
      });
      toast({ title: t.tradovate.importButton });
      onDone();
    } catch (err: any) {
      toast({
        title: t.tradovate.errorGeneric,
        description: err?.message || undefined,
        variant: "destructive",
      });
      setSubmitting(false);
    }
  };

  // Loading: styled skeleton-ish state with an explanatory line (fetching the
  // account list + balances from Tradovate can take a few seconds).
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
        <Loader2 className="h-7 w-7 animate-spin text-primary" />
        <p className="text-sm font-medium">{t.tradovate.loadingAccountsTitle}</p>
        <p className="text-xs text-muted-foreground max-w-xs">
          {t.tradovate.loadingAccountsHint}
        </p>
      </div>
    );
  }

  // Error: the query failed (e.g. Tradovate API rejected the token). Show a
  // message + retry instead of an infinite spinner.
  //
  // Cas particulier des soldes illisibles : le serveur refuse alors l'import
  // (cf. tradovateBalanceIntegrity) plutôt que de retomber sur un solde à 0, qui
  // faisait détecter le plus petit palier et le gelait dans `initialBalance`. Le
  // message porte un préfixe machine pour qu'on distingue le quota saturé — qui
  // se résout en attendant — d'une indisponibilité de cause inconnue. On ne
  // propose PAS « Réessayer » sur le rate limit : un nouvel appel immédiat ne
  // ferait qu'ajouter à la saturation d'un quota partagé entre utilisateurs.
  if (error) {
    const raw = (error as any)?.message ?? "";
    const isRateLimited = raw.includes("TRADOVATE_RATE_LIMITED:");
    const isBalanceUnavailable = raw.includes("TRADOVATE_BALANCE_UNAVAILABLE:");

    const title = isRateLimited
      ? t.tradovate.loadAccountsRateLimitedTitle
      : isBalanceUnavailable
        ? t.tradovate.loadAccountsBalanceErrorTitle
        : t.tradovate.loadAccountsErrorTitle;
    const hint = isRateLimited
      ? t.tradovate.loadAccountsRateLimitedHint
      : isBalanceUnavailable
        ? t.tradovate.loadAccountsBalanceErrorHint
        : t.tradovate.loadAccountsErrorHint;

    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
        <AlertCircle className="h-7 w-7 text-destructive" />
        <p className="text-sm font-medium">{title}</p>
        <p className="text-xs text-muted-foreground max-w-sm">{hint}</p>
        {!isRateLimited && (
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="mr-2 h-4 w-4" />
            {t.tradovate.retry}
          </Button>
        )}
      </div>
    );
  }

  // Empty: Tradovate returned zero accounts for this connection.
  if (!rows || rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
        <Inbox className="h-7 w-7 text-muted-foreground" />
        <p className="text-sm font-medium">{t.tradovate.noAccountsTitle}</p>
        <p className="text-xs text-muted-foreground max-w-xs">
          {t.tradovate.noAccountsHint}
        </p>
      </div>
    );
  }

  const statusLabels: Record<AccountStatus, string> = {
    challenge: t.tradingAccounts.statusChallenge,
    pa: t.tradingAccounts.statusPa,
    live: t.tradingAccounts.statusLive,
    demo: t.tradingAccounts.statusDemo,
    inactive: t.tradingAccounts.statusInactive,
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <h2 className="text-lg font-semibold">{t.tradovate.reviewTitle}</h2>
        <p className="text-sm text-muted-foreground">
          {t.tradovate.reviewSubtitle.replace("{count}", String(rows.length))}
        </p>
      </div>

      <div className="space-y-3">
        {rows.map((row, i) => (
          <div
            key={row.tradovateAccountId}
            className={cn(
              "rounded-lg border border-border bg-card p-3",
              row.confidence === "low" && "border-[hsl(var(--warning))]/50",
            )}
          >
            {/* 4 colonnes quand le prix est demandé (compte neuf), sinon 3 :
                sur 3 colonnes fixes la colonne prix passait à la ligne seule.
                Colonnes PONDÉRÉES : l'identifiant broker (~20 caractères mono)
                et le libellé « type - palier » étaient tronqués à parts égales,
                alors que le statut et le prix tiennent dans bien moins. */}
            <div
              className={cn(
                "grid grid-cols-1 gap-3 sm:items-end",
                !row.isKnown
                  ? "sm:grid-cols-2 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,0.8fr)]"
                  : "sm:grid-cols-[minmax(0,1.3fr)_minmax(0,1.3fr)_minmax(0,1fr)]",
              )}
            >
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">{row.name}</Label>
                <Input
                  className="font-mono text-sm"
                  value={row.edgelyAccountName}
                  disabled={row.isKnown}
                  onChange={(e) => updateRow(i, { edgelyAccountName: e.target.value })}
                />
              </div>

              {tierCount > 0 && (
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">{t.tradovate.tierLabel}</Label>
                  <Select
                    value={row.tierId ?? undefined}
                    disabled={row.isKnown}
                    onValueChange={(v) =>
                      updateRow(i, {
                        tierId: v,
                        // Direct funded n'a jamais de phase de challenge : un
                        // changement de palier vers un tel type de compte doit
                        // aussi corriger un statut "challenge" déjà choisi,
                        // sinon la soumission se ferait refuser par le serveur.
                        ...(directFundedTierIds.has(v) && row.status === "challenge"
                          ? { status: "pa" as AccountStatus }
                          : {}),
                      })
                    }
                  >
                    <SelectTrigger
                      className={cn(
                        row.confidence === "low" &&
                          "border-[hsl(var(--warning))] focus:ring-[hsl(var(--warning))]",
                      )}
                    >
                      {/* Enfant rendu en RETOUR de Radix : passer `undefined`
                          quand le palier est inconnu de la map effaçait le
                          libellé du trigger, y compris après un choix
                          explicite. On ne fournit un enfant que lorsqu'on a
                          réellement un libellé à afficher ; sinon on laisse
                          SelectValue rendre la valeur sélectionnée lui-même. */}
                      {row.tierId && tierDisplayLabel.has(row.tierId) ? (
                        <SelectValue>{tierDisplayLabel.get(row.tierId)}</SelectValue>
                      ) : (
                        <SelectValue placeholder={t.tradovate.tierPlaceholder} />
                      )}
                    </SelectTrigger>
                    <SelectContent>
                      {tierGroups.map((group) => (
                        <SelectGroup key={group.id}>
                          <SelectLabel>{group.name}</SelectLabel>
                          {group.tiers.map((tier) => (
                            <SelectItem key={tier.id} value={tier.id}>
                              {tier.label}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      ))}
                    </SelectContent>
                  </Select>
                  {row.confidence === "low" && (
                    <p className="text-xs text-[hsl(var(--warning))]">
                      {t.tradovate.lowConfidenceHint}
                    </p>
                  )}
                </div>
              )}

              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">{t.tradovate.statusLabel}</Label>
                <Select
                  value={row.status}
                  disabled={row.isKnown}
                  onValueChange={(v) => {
                    userEditedStatus.current.add(i);
                    updateRow(i, { status: v as AccountStatus });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.filter(
                      (status) =>
                        status !== "challenge" || !row.tierId || !directFundedTierIds.has(row.tierId),
                    ).map((status) => (
                      <SelectItem key={status} value={status}>
                        {statusLabels[status]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Prix payé : uniquement pour un compte inconnu d'Edgely. Un
                  compte rattaché (reconnexion, passage en funded) porte déjà
                  son coût, redemander le fausserait. */}
              {!row.isKnown && (
                <div className="space-y-1">
                  <Label
                    htmlFor={`tradovate-cost-${row.tradovateAccountId}`}
                    className="text-xs text-muted-foreground"
                  >
                    {/* Le prix saisi ne devient un abonnement récurrent que
                        pour une ligne "challenge" sur un palier subscription
                        (propFirmSubscription.ts : `subscribes` exige
                        `status === "challenge"`). Une ligne déjà "pa" stocke ce
                        même prix comme un coût ponctuel : le libellé doit le
                        refléter, pas juste le modèle du palier. */}
                    {row.tierId && row.status === "challenge" && isSubscription(tierPricingModel.get(row.tierId))
                      ? t.tradingAccounts.subscriptionPriceLabel
                      : t.tradovate.costLabel}
                  </Label>
                  <Input
                    id={`tradovate-cost-${row.tradovateAccountId}`}
                    className="font-mono text-sm"
                    inputMode="decimal"
                    placeholder={t.tradovate.costPlaceholder}
                    value={row.challengeCost}
                    onChange={(e) => updateRow(i, { challengeCost: e.target.value })}
                  />
                </div>
              )}
            </div>

            <div className="mt-3 space-y-1">
              <Label className="text-xs text-muted-foreground">{t.tradovate.groupLabel}</Label>
              <Select
                value={row.group}
                disabled={row.isKnown}
                onValueChange={(v) => updateRow(i, { group: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_GROUP_VALUE}>{t.tradovate.groupNone}</SelectItem>
                  {groupOptions.map((g) => (
                    <SelectItem key={g.id} value={g.id}>
                      {g.name}
                    </SelectItem>
                  ))}
                  {!row.isKnown && <SelectItem value={NEW_GROUP_VALUE}>{t.tradovate.groupNew}</SelectItem>}
                </SelectContent>
              </Select>
            </div>

            <p className="mt-2 text-xs text-muted-foreground">
              {row.isKnown ? t.tradovate.knownAccountHint : t.tradovate.costHint}
            </p>

            {/* Suite d'un challenge : le rattachement automatique de la sync ne
                passe plus en temps réel, c'est donc ici que le lien se pose.
                Un challenge déjà choisi sur une autre ligne n'est pas proposé. */}
            {!row.isKnown && canFollowChallenge(row.status) && predecessorOptions.length > 0 && (
              <div className="mt-3 space-y-1">
                <Label className="text-xs text-muted-foreground">{t.fundedLineage.importLabel}</Label>
                <Select
                  value={row.previousAccountId ?? "none"}
                  onValueChange={(v) => updateRow(i, { previousAccountId: v === "none" ? null : v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t.fundedLineage.importNone}</SelectItem>
                    {predecessorOptions
                      .filter(
                        (c) =>
                          c.id === row.previousAccountId ||
                          !rows.some((other, j) => j !== i && canFollowChallenge(other.status) && other.previousAccountId === c.id),
                      )
                      .map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          <PredecessorLabel option={c} suggested={c.id === accounts?.[i]?.suggestedPredecessorId} />
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">{t.fundedLineage.importHint}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Nom saisi une seule fois : toutes les lignes sur « Nouveau groupe »
          rejoignent le même groupe. */}
      {hasNewGroupRow && (
        <div className="space-y-2 rounded-lg border border-border bg-card p-3">
          <Label htmlFor="tradovate-new-group-name" className="text-sm">
            {t.tradovate.newGroupNameLabel}
          </Label>
          <Input
            id="tradovate-new-group-name"
            value={groupName}
            placeholder={t.tradovate.groupNamePlaceholder}
            onChange={(e) => setGroupName(e.target.value)}
          />
        </div>
      )}

      {strategies.length > 0 && (
        <div className="space-y-2 rounded-lg border border-border bg-card p-3">
          <Label className="text-sm">{t.tradovate.strategyLabel}</Label>
          <Select
            value={strategyId ?? "none"}
            onValueChange={(v) => setStrategyId(v === "none" ? null : v)}
          >
            <SelectTrigger>
              <SelectValue placeholder={t.tradovate.strategyNone} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{t.tradovate.strategyNone}</SelectItem>
              {strategies.map((s: { id: string; name: string; color?: string | null }) => (
                <SelectItem key={s.id} value={s.id}>
                  <span className="flex items-center gap-2">
                    {s.color && (
                      <span
                        className="h-2 w-2 rounded-full shrink-0"
                        style={{ backgroundColor: s.color }}
                      />
                    )}
                    {s.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="flex justify-end pt-2">
        <Button onClick={handleImport} disabled={submitting || (hasNewGroupRow && !groupName.trim())}>
          {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {submitting ? t.tradovate.importing : t.tradovate.importButton}
        </Button>
      </div>
    </div>
  );
}

